// KI-Assistent-Panel für die Ticket-Detailansicht (Etappe 4 — Aktions-Ebene; Etappe 5b — helles Card-Restyle).
// Zeigt Erkannte Kategorie + Dringlichkeit, die Liste der ticket_aktionen mit den Buttons
// "Prüfen & senden" (Mail-Typen), "Ausführen" (interne Typen), "Verwerfen"/"Wiederherstellen"
// sowie "Vorschläge erzeugen", "+ Wiedervorlage" und "Antwortvorschlag erstellen" (gezielter
// antwort_absender-Entwurf über die Aktions-Engine).
import React, { useCallback, useEffect, useState } from 'react';
import {
  ArrowCircleRight, CalendarCheck, CheckSquare, EnvelopeSimple, FileText, Plus, Scales, Sparkle, SpinnerGap, UserCircle, Users, Wrench, X,
  type Icon,
} from '@phosphor-icons/react';
import { kiProzent, PRIO_FARBE } from '../../../lib/tickets/darstellung';
import { supabase } from '../../../lib/supabase/client';
import EmailVerfassen from '../emails/EmailVerfassen';
import { useIslandActions } from '../../../components/island/IslandProvider';
import { ladeAuftragsDaten } from '../../../lib/tickets/ladeAuftragsDaten';
import { auftragsPlatzhalterWerte } from '../../../lib/tickets/auftragsMail';
// Side-effect-Import: registriert alle 6 Aktionstyp-Module auf der Default-Registry.
import '../../../lib/aktionen/module';
import { ladeAktionenFuerTicket, speichereAktionen, setzeAktionStatus } from '../../../lib/aktionen/api';
import { baueAktionsKontext } from '../../../lib/aktionen/kontext';
import { aktionsRegistry } from '../../../lib/aktionen/registry';
import { antwortAbsenderModul } from '../../../lib/aktionen/typen/antwortAbsender';
import { verknuepfeTicketMitEmail } from '../../../lib/ki/ticketLink';
import type { AktionEntwurf, AktionKontext, TicketAktion } from '../../../lib/aktionen/types';
import { PRIO_PILL } from '../../../components/ui/statusStyles';
import { MAIL_FORM_META } from '../../../components/island/form/mailMeta';

// Ticket-Basis + KI-Snapshot-Felder, die das Panel benötigt. Struktureller Subtyp
// des Ticket-Interfaces aus TicketDetailsSidebar.tsx — dort direkt zuweisbar.
export type TicketMitKi = {
  id: string;
  ticket_nummer: string;
  titel: string;
  objekt_id: string | null;
  mieter_id: string | null;
  kategorie_id: string | null;
  unterkategorie_id?: string | null;
  prioritaet: string;
  einheit_id?: string | null;
  absender_typ?: string | null;
  erkannte_aufgabe?: string | null;
  naechster_schritt?: string | null;
  bearbeiter_vorschlag_id?: string | null;
  bearbeiter_vorschlag_typ?: 'profile' | 'team' | null;
  dienstleister_vorschlag_id?: string | null;
  ki_confidence?: number | null;
  ist_rechtlich_sensibel?: boolean | null;
  quelle_email_id?: string | null;
};

interface TicketKiPanelProps {
  ticket: TicketMitKi;
  userProfile?: any;
  onUpdate: () => void;
}

const TYP_CONFIG: Record<string, { icon: Icon; label: string; istMail: boolean; farbe: string }> = {
  handwerker_beauftragen: { icon: Wrench, label: 'Handwerker beauftragen', istMail: true, farbe: 'var(--warn)' },
  angebot_anfordern: { icon: FileText, label: 'Angebot anfordern', istMail: true, farbe: 'var(--info)' },
  antwort_absender: { icon: EnvelopeSimple, label: 'Antwort an Absender', istMail: true, farbe: 'var(--acc)' },
  an_team: { icon: Users, label: 'An Team', istMail: false, farbe: 'var(--info)' },
  wiedervorlage: { icon: CalendarCheck, label: 'Wiedervorlage', istMail: false, farbe: '#8a63d2' },
  interne_aufgabe: { icon: CheckSquare, label: 'Interne Aufgabe', istMail: false, farbe: 'var(--ok)' },
};

const STATUS_PILL: Record<string, { farbe: string; durch?: boolean }> = {
  entwurf: { farbe: 'var(--ink2)' },
  freigegeben: { farbe: 'var(--info)' },
  gesendet: { farbe: 'var(--ok)' },
  erledigt: { farbe: 'var(--ok)' },
  verworfen: { farbe: 'var(--ink3)', durch: true },
};
const LILA = '#8a63d2';

const STATUS_LABEL: Record<string, string> = {
  entwurf: 'Entwurf',
  freigegeben: 'Freigegeben',
  gesendet: 'Gesendet',
  erledigt: 'Erledigt',
  verworfen: 'Verworfen',
};

const TicketKiPanel: React.FC<TicketKiPanelProps> = ({ ticket, userProfile, onUpdate }) => {
  const [aktionen, setAktionen] = useState<TicketAktion[]>([]);
  const [aktionLaedt, setAktionLaedt] = useState(false);
  const [fehler, setFehler] = useState<Record<string, string>>({});

  const [kategorieName, setKategorieName] = useState<string | null>(null);

  const [erzeugenFehler, setErzeugenFehler] = useState<string | null>(null);
  const [erzeugenHinweis, setErzeugenHinweis] = useState<string | null>(null);

  const [zeigeWiedervorlageForm, setZeigeWiedervorlageForm] = useState(false);
  const [wvDatum, setWvDatum] = useState('');
  const [wvNotiz, setWvNotiz] = useState('');
  const [wvFehler, setWvFehler] = useState<string | null>(null);

  const [antwortLaedt, setAntwortLaedt] = useState(false);
  const [antwortHinweis, setAntwortHinweis] = useState<string | null>(null);

  // Mail-Aktionen öffnen das Verfassen-Formular in der Pille (openForm) — kein eigenes Modal.
  const { openForm, closeForm } = useIslandActions();

  const ladeAktionen = useCallback(async () => {
    try {
      const data = await ladeAktionenFuerTicket(ticket.id);
      setAktionen(data);
    } catch (e) {
      console.error('Aktionen konnten nicht geladen werden', e);
    }
  }, [ticket.id]);

  useEffect(() => { ladeAktionen(); }, [ladeAktionen]);

  // Erkannte Kategorie: bevorzugt die Unterkategorie (spezifischste KI-Einschätzung), sonst die Hauptkategorie.
  useEffect(() => {
    let cancelled = false;
    const katId = ticket.unterkategorie_id || ticket.kategorie_id;
    if (!katId) { setKategorieName(null); return; }
    supabase.from('ticket_kategorien').select('name').eq('id', katId).maybeSingle()
      .then(({ data }) => { if (!cancelled) setKategorieName(data?.name ?? null); });
    return () => { cancelled = true; };
  }, [ticket.unterkategorie_id, ticket.kategorie_id]);

  // Bearbeiter- und Dienstleister-Vorschlag der KI (vorhandene Felder am Ticket) als Namen
  const [bearbeiterName, setBearbeiterName] = useState<string | null>(null);
  const [dienstleisterName, setDienstleisterName] = useState<string | null>(null);
  useEffect(() => {
    let cancelled = false;
    const id = ticket.bearbeiter_vorschlag_id;
    if (!id) { setBearbeiterName(null); return; }
    const anfrage = ticket.bearbeiter_vorschlag_typ === 'team'
      ? supabase.from('teams').select('name').eq('id', id).maybeSingle().then(({ data }) => (data?.name as string | undefined) ?? null)
      : supabase.from('profiles').select('full_name').eq('id', id).maybeSingle().then(({ data }) => (data?.full_name as string | undefined) ?? null);
    void anfrage.then(n => { if (!cancelled) setBearbeiterName(n); });
    return () => { cancelled = true; };
  }, [ticket.bearbeiter_vorschlag_id, ticket.bearbeiter_vorschlag_typ]);
  useEffect(() => {
    let cancelled = false;
    const id = ticket.dienstleister_vorschlag_id;
    if (!id) { setDienstleisterName(null); return; }
    supabase.from('dienstleister').select('name').eq('id', id).maybeSingle()
      .then(({ data }) => { if (!cancelled) setDienstleisterName((data?.name as string | undefined) ?? null); });
    return () => { cancelled = true; };
  }, [ticket.dienstleister_vorschlag_id]);

  const setAktionFehler = (aktionId: string, msg: string | null) => {
    setFehler((f) => {
      const next = { ...f };
      if (msg) next[aktionId] = msg; else delete next[aktionId];
      return next;
    });
  };

  // Ticket-Basis für die Aktions-Engine (Muster wie erstellt/gebaut in handleVorschlaegeErzeugen).
  const buildTicketBasis = useCallback((): AktionKontext['ticket'] => ({
    id: ticket.id,
    titel: ticket.titel,
    ticket_nummer: ticket.ticket_nummer,
    objekt_id: ticket.objekt_id,
    mieter_id: ticket.mieter_id,
    einheit_id: ticket.einheit_id ?? null,
    kategorie_id: ticket.kategorie_id,
    prioritaet: ticket.prioritaet,
    absender_typ: ticket.absender_typ ?? null,
    erkannte_aufgabe: ticket.erkannte_aufgabe ?? null,
    naechster_schritt: ticket.naechster_schritt ?? null,
    dienstleister_vorschlag_id: ticket.dienstleister_vorschlag_id ?? null,
    ki_confidence: ticket.ki_confidence ?? null,
    ist_rechtlich_sensibel: ticket.ist_rechtlich_sensibel ?? false,
    bearbeiter_vorschlag_id: ticket.bearbeiter_vorschlag_id ?? null,
    bearbeiter_vorschlag_typ: ticket.bearbeiter_vorschlag_typ ?? null,
  }), [ticket]);

  // Aktionstyp → E-Mail-Vorlagen-Trigger: diese Aktionen nutzen die im Vorlagen-Tab
  // zugeordnete Vorlage (Platzhalter aus den Ticket-/Auftragsdaten) — gleiche Mail wie
  // der Pillen-Flow "Handwerker beauftragen". Der gespeicherte Entwurf bleibt Fallback.
  const TRIGGER_BY_TYP: Record<string, string> = {
    handwerker_beauftragen: 'handwerker_beauftragung',
    angebot_anfordern: 'angebot_anfordern',
  };

  // "Prüfen & senden" (Mail-Typen): aktives Konto laden, EmailVerfassen mit Aktions-Entwurf
  // in der PILLE öffnen (wie Mieter-/Eigentümer-Nachricht und Handwerker-Beauftragung).
  // aktion wandert als Closure-Wert ins openForm-Element — kein Stale-State.
  const handlePruefenSenden = async (aktion: TicketAktion) => {
    setAktionLaedt(true);
    setAktionFehler(aktion.id, null);
    try {
      const { data: konto, error } = await supabase
        .from('email_konten').select('id, email_adresse, bezeichnung').eq('aktiv', true).limit(1).maybeSingle();
      if (error) throw error;
      if (!konto) { setAktionFehler(aktion.id, 'Kein E-Mail-Konto konfiguriert.'); return; }

      // Auftrags-Platzhalter für vorlagen-getriebene Typen (Handwerker/Angebot) laden.
      const vorlagenTrigger = TRIGGER_BY_TYP[aktion.typ];
      let vorlagenWerte: Record<string, string> | undefined;
      if (vorlagenTrigger) {
        try {
          const { daten } = await ladeAuftragsDaten(ticket.id);
          vorlagenWerte = auftragsPlatzhalterWerte(daten);
        } catch (e) {
          console.warn('Auftragsdaten für Vorlage nicht ladbar — Fallback auf Entwurfstext', e);
        }
      }

      // Entwurfstext NICHT über draft.text_inhalt (rendert doppelt: roh + im Bogen), sondern
      // über autoText — greift nur, wenn keine zugeordnete Vorlage existiert. Abschließende
      // Grußformel (+ max. 2 kurze Folgezeilen wie "Zentrales Postfach") abschneiden:
      // die Signatur bringt die Grußformel bereits mit.
      const entwurfText = (aktion.entwurf_text ?? '')
        .replace(/\n\s*Mit freundlichen Grüßen(\s*\n[^\n]{0,60}){0,2}\s*$/i, '')
        .trimEnd();
      openForm(
        <EmailVerfassen
          variant="pille"
          konto={{ id: konto.id, email_adresse: konto.email_adresse, bezeichnung: konto.bezeichnung || konto.email_adresse }}
          draft={{
            an: aktion.empfaenger ? [aktion.empfaenger] : [],
            betreff: aktion.betreff ?? '',
            text_inhalt: '',
            anhaenge: ((aktion.payload as any)?.anhaenge as { name: string; path: string; size: number }[]) ?? [],
          }}
          autoVorlageTrigger={vorlagenTrigger}
          autoVorlageWerte={vorlagenWerte}
          autoText={entwurfText}
          onClose={() => closeForm()}
          onSent={(info) => { closeForm(); handleMailGesendet(aktion, info?.emailId ?? null); }}
        />,
        TYP_CONFIG[aktion.typ]?.label ?? 'E-Mail senden',
        640, MAIL_FORM_META,
      );
    } catch (e: any) {
      setAktionFehler(aktion.id, e?.message || 'E-Mail-Konto konnte nicht geladen werden.');
    } finally {
      setAktionLaedt(false);
    }
  };

  // Nach erfolgreichem Versand: gesendete Mail ans Ticket verknüpfen (erscheint als
  // Thread-Eintrag), Status 'gesendet' + modulspezifische Nachbereitung (Historie/Status;
  // Volltext-Kommentar nur noch als Fallback ohne Verknüpfung).
  const handleMailGesendet = async (aktion: TicketAktion, emailId: string | null) => {
    setAktionLaedt(true);
    let emailVerknuepft = false;
    if (emailId) {
      try {
        await verknuepfeTicketMitEmail(ticket.id, emailId);
        emailVerknuepft = true;
      } catch (e) {
        console.warn('Gesendete Mail konnte nicht verknüpft werden', e);
      }
    }
    try {
      await setzeAktionStatus(aktion.id, 'gesendet', userProfile?.id ?? null);
      try {
        await aktionsRegistry.lade(aktion.typ)?.nachVersand?.(aktion, userProfile?.id ?? null, { emailVerknuepft });
      } catch (e) {
        console.warn('nachVersand fehlgeschlagen', e);
      }
    } catch (e: any) {
      setAktionFehler(aktion.id, e?.message || 'Status konnte nicht aktualisiert werden.');
    } finally {
      setAktionLaedt(false);
      await ladeAktionen();
      onUpdate();
    }
  };

  // "Ausführen" (interne Typen): modulspezifisches ausfuehren(), bei Erfolg Status 'erledigt'.
  const handleAusfuehren = async (aktion: TicketAktion) => {
    setAktionLaedt(true);
    setAktionFehler(aktion.id, null);
    try {
      const modul = aktionsRegistry.lade(aktion.typ);
      const res = await modul?.ausfuehren?.(aktion);
      if (res?.ok) {
        await setzeAktionStatus(aktion.id, 'erledigt');
      } else {
        setAktionFehler(aktion.id, res?.fehler || 'Ausführen fehlgeschlagen.');
      }
    } catch (e: any) {
      setAktionFehler(aktion.id, e?.message || 'Ausführen fehlgeschlagen.');
    } finally {
      setAktionLaedt(false);
      await ladeAktionen();
      onUpdate();
    }
  };

  const handleVerwerfen = async (aktion: TicketAktion) => {
    setAktionLaedt(true);
    setAktionFehler(aktion.id, null);
    try {
      await setzeAktionStatus(aktion.id, 'verworfen');
    } catch (e: any) {
      setAktionFehler(aktion.id, e?.message || 'Verwerfen fehlgeschlagen.');
    } finally {
      setAktionLaedt(false);
      await ladeAktionen();
    }
  };

  const handleWiederherstellen = async (aktion: TicketAktion) => {
    setAktionLaedt(true);
    setAktionFehler(aktion.id, null);
    try {
      await setzeAktionStatus(aktion.id, 'entwurf');
    } catch (e: any) {
      setAktionFehler(aktion.id, e?.message || 'Wiederherstellen fehlgeschlagen.');
    } finally {
      setAktionLaedt(false);
      await ladeAktionen();
    }
  };

  // "Vorschläge erzeugen": Kontext bauen (Stammdaten laden) + alle Module durchlaufen + speichern.
  const handleVorschlaegeErzeugen = async () => {
    setAktionLaedt(true);
    setErzeugenFehler(null);
    setErzeugenHinweis(null);
    try {
      const ctx = await baueAktionsKontext(buildTicketBasis());
      const entwuerfe = aktionsRegistry.erzeugeEntwuerfe(ctx);
      // Duplikat-Guard: Typen, für die bereits eine nicht-verworfene Aktion existiert
      // (auch schon 'gesendet'/'erledigt'), nicht erneut vorschlagen — sonst könnte z.B.
      // ein zweiter Handwerker-Auftrag an denselben Empfänger erzeugt werden.
      const bestehende = await ladeAktionenFuerTicket(ticket.id);
      const belegteTypen = new Set(bestehende.filter((a) => a.status !== 'verworfen').map((a) => a.typ));
      const neue = entwuerfe.filter((e) => !belegteTypen.has(e.typ));
      if (neue.length === 0) {
        setErzeugenHinweis(entwuerfe.length > 0 ? 'Vorschläge bereits vorhanden (siehe Aktionen)' : 'Keine passenden Vorschläge');
      } else {
        await speichereAktionen(neue, ticket.id, userProfile?.id ?? null);
        await ladeAktionen();
      }
    } catch (e: any) {
      setErzeugenFehler(e?.message || 'Vorschläge konnten nicht erzeugt werden.');
    } finally {
      setAktionLaedt(false);
    }
  };

  // "+ Wiedervorlage" Mini-Form: legt einen sofort ausführbaren Entwurf an (Ausführen setzt tickets.wiedervorlage_am).
  const handleWiedervorlageAnlegen = async () => {
    if (!wvDatum) return;
    setAktionLaedt(true);
    setWvFehler(null);
    try {
      const entwurf: AktionEntwurf = {
        typ: 'wiedervorlage',
        freigabe_erforderlich: false,
        ist_rechtlich_sensibel: false,
        empfaenger: null,
        empfaenger_kontakt_id: null,
        betreff: null,
        entwurf_text: `Wiedervorlage am ${wvDatum}${wvNotiz ? ': ' + wvNotiz : ''}`,
        payload: { wiedervorlage_am: wvDatum, notiz: wvNotiz || null },
        ki_confidence: null,
      };
      await speichereAktionen([entwurf], ticket.id, userProfile?.id ?? null);
      setWvDatum('');
      setWvNotiz('');
      setZeigeWiedervorlageForm(false);
      await ladeAktionen();
    } catch (e: any) {
      setWvFehler(e?.message || 'Wiedervorlage konnte nicht angelegt werden.');
    } finally {
      setAktionLaedt(false);
    }
  };

  // "Antwortvorschlag erstellen": erzeugt gezielt einen antwort_absender-Entwurf (statt alle Module durchzulaufen).
  const handleAntwortvorschlagErstellen = async () => {
    setAntwortLaedt(true);
    setAntwortHinweis(null);
    try {
      const ctx = await baueAktionsKontext(buildTicketBasis());
      const entwurf = antwortAbsenderModul.erzeugen(ctx);
      if (!entwurf) {
        setAntwortHinweis('Kein Empfänger bekannt');
        return;
      }
      await speichereAktionen([entwurf], ticket.id, userProfile?.id ?? null);
      await ladeAktionen();
    } catch (e: any) {
      setAntwortHinweis(e?.message || 'Antwortvorschlag konnte nicht erstellt werden.');
    } finally {
      setAntwortLaedt(false);
    }
  };

  const hatOffenenEntwurf = aktionen.some((a) => a.status === 'entwurf');
  const bestehendeAntwortAktion = aktionen.some((a) => a.typ === 'antwort_absender' && a.status !== 'verworfen');
  const antwortDisabled = aktionLaedt || antwortLaedt || !!ticket.ist_rechtlich_sensibel || bestehendeAntwortAktion;

  const prozent = kiProzent(ticket.ki_confidence);
  const prioFarbe = PRIO_FARBE[ticket.prioritaet] ?? 'var(--warn)';
  const knopf = (art: 'voll' | 'leise' | 'lila'): React.CSSProperties => ({
    height: 30, padding: '0 12px', gap: 6, borderRadius: 15, border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, fontWeight: 800,
    display: 'inline-flex', alignItems: 'center',
    ...(art === 'voll' ? { background: 'var(--acc-fill)', color: 'var(--acc-ink)', boxShadow: 'var(--acc-sh)' }
      : art === 'lila' ? { background: 'linear-gradient(160deg,#a78bfa,#7c4ddb)', color: '#fff', boxShadow: '0 8px 18px -10px #7c4ddb' }
        : { background: 'var(--fill2)', color: 'var(--ink2)' }),
  });
  const kachel: React.CSSProperties = { borderRadius: 14, padding: '10px 12px', background: 'var(--fill)', boxShadow: 'inset 0 0 0 1px var(--line)', minWidth: 0 };
  const feld: React.CSSProperties = { height: 34, borderRadius: 10, padding: '0 10px', border: 'none', outline: 'none', background: 'var(--bg)', boxShadow: 'inset 0 0 0 1px var(--line)', color: 'var(--ink)', fontFamily: 'inherit', fontSize: 13 };

  return (
    <div className="flex flex-col" style={{ gap: 12 }}>
      {hatOffenenEntwurf && (
        <span className="self-start" style={{ height: 20, padding: '0 8px', borderRadius: 10, fontSize: 10.5, fontWeight: 800, lineHeight: '20px', background: `color-mix(in srgb, ${LILA} 18%, transparent)`, color: LILA }}>
          Neu
        </span>
      )}

      {/* Erkannte Kategorie + Dringlichkeit */}
      <div className="grid" style={{ gap: 8, gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))' }}>
        <div style={kachel}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>Erkannte Kategorie</div>
          <div style={{ fontSize: 14, fontWeight: 800, marginTop: 2 }}>{kategorieName || '–'}</div>
          {prozent !== null && (
            <div aria-hidden style={{ height: 4, borderRadius: 2, background: 'var(--fill3)', marginTop: 8, overflow: 'hidden' }}>
              <div style={{ width: `${Math.min(100, prozent)}%`, height: '100%', borderRadius: 2, background: 'var(--ok)' }} />
            </div>
          )}
        </div>
        <div style={kachel}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--ink3)' }}>Dringlichkeit</div>
          <div style={{ fontSize: 14, fontWeight: 800, marginTop: 2, color: prioFarbe }}>{PRIO_PILL[ticket.prioritaet]?.label ?? ticket.prioritaet}</div>
          {prozent !== null && <div style={{ fontSize: 11.5, fontWeight: 600, color: 'var(--ink3)', marginTop: 2 }}>{prozent} % Sicherheit</div>}
        </div>
      </div>

      {ticket.ist_rechtlich_sensibel && (
        <div className="flex items-center" style={{ gap: 8, padding: '8px 12px', borderRadius: 12, background: 'var(--bad-soft)', color: 'var(--bad)', fontSize: 13, fontWeight: 800 }}>
          <Scales size={16} weight="fill" aria-hidden /> Rechtlich sensibel – Freigabe vor Versand
        </div>
      )}

      {ticket.naechster_schritt && (
        <div className="flex items-start" style={{ ...kachel, gap: 8 }}>
          <ArrowCircleRight size={17} weight="fill" color={LILA} style={{ flexShrink: 0, marginTop: 1 }} aria-hidden />
          <span style={{ fontSize: 13, lineHeight: 1.45 }}><b>Nächster Schritt:</b> {ticket.naechster_schritt}</span>
        </div>
      )}

      {(bearbeiterName || dienstleisterName) && (
        <div className="flex flex-wrap" style={{ gap: 6 }}>
          {bearbeiterName && (
            <span data-tip="Bearbeiter-Vorschlag – Von der KI empfohlene zuständige Person" className="inline-flex items-center"
              style={{ height: 28, padding: '0 10px', gap: 6, borderRadius: 14, fontSize: 12.5, fontWeight: 700, background: 'var(--fill2)' }}>
              <UserCircle size={15} weight="fill" color="var(--ink2)" aria-hidden />{bearbeiterName}
            </span>
          )}
          {dienstleisterName && (
            <span data-tip="Dienstleister-Vorschlag – Passender Fachbetrieb für diese Kategorie" className="inline-flex items-center"
              style={{ height: 28, padding: '0 10px', gap: 6, borderRadius: 14, fontSize: 12.5, fontWeight: 700, background: 'var(--fill2)' }}>
              <Wrench size={14} weight="fill" color="var(--warn)" aria-hidden />{dienstleisterName}
            </span>
          )}
        </div>
      )}

      {/* Empfohlene Aktionen */}
      {aktionen.length > 0 && (
        <div className="flex flex-col" style={{ gap: 8 }}>
          <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '0.06em', textTransform: 'uppercase', color: 'var(--ink3)' }}>Empfohlene Aktionen</span>
          {aktionen.map((aktion) => {
            const cfg = TYP_CONFIG[aktion.typ] || { icon: FileText, label: aktion.typ, istMail: false, farbe: 'var(--ink2)' };
            const TypIcon = cfg.icon;
            const pill = STATUS_PILL[aktion.status] ?? STATUS_PILL.entwurf!;
            return (
              <div key={aktion.id} className="flex flex-col" style={{ ...kachel, gap: 6, opacity: aktion.status === 'verworfen' ? 0.6 : 1 }}>
                <div className="flex items-center justify-between" style={{ gap: 8 }}>
                  <span className="flex min-w-0 items-center" style={{ gap: 8 }}>
                    <span aria-hidden className="inline-flex shrink-0 items-center justify-center" style={{ width: 26, height: 26, borderRadius: 8, background: `color-mix(in srgb, ${cfg.farbe} 16%, transparent)`, color: cfg.farbe }}>
                      <TypIcon size={14} weight="fill" />
                    </span>
                    <span className="truncate" style={{ fontSize: 13.5, fontWeight: 800, textDecoration: pill.durch ? 'line-through' : 'none' }}>{cfg.label}</span>
                  </span>
                  <span className="shrink-0" style={{ height: 20, padding: '0 8px', borderRadius: 10, fontSize: 10.5, fontWeight: 800, lineHeight: '20px',
                    background: `color-mix(in srgb, ${pill.farbe} 14%, transparent)`, color: pill.farbe, textDecoration: pill.durch ? 'line-through' : 'none' }}>
                    {STATUS_LABEL[aktion.status] || aktion.status}
                  </span>
                </div>
                {(aktion.empfaenger || aktion.betreff) && (
                  <span className="truncate" style={{ fontSize: 12, color: 'var(--ink3)', paddingLeft: 34 }}>
                    {aktion.empfaenger}{aktion.empfaenger && aktion.betreff ? ' · ' : ''}{aktion.betreff}
                  </span>
                )}
                {aktion.status === 'entwurf' && (
                  <div className="flex flex-wrap items-center" style={{ gap: 6, paddingLeft: 34 }}>
                    <button onClick={() => (cfg.istMail ? handlePruefenSenden(aktion) : handleAusfuehren(aktion))} disabled={aktionLaedt}
                      data-tip={`${cfg.istMail ? 'Prüfen & senden' : 'Ausführen'} – Empfehlung des KI-Assistenten ausführen`} style={{ ...knopf('voll'), opacity: aktionLaedt ? 0.5 : 1 }}>
                      {cfg.istMail ? 'Prüfen & senden' : 'Ausführen'}
                    </button>
                    <button onClick={() => handleVerwerfen(aktion)} disabled={aktionLaedt} data-tip="Verwerfen – Empfehlung ausblenden" style={{ ...knopf('leise'), opacity: aktionLaedt ? 0.5 : 1 }}>
                      Verwerfen
                    </button>
                  </div>
                )}
                {aktion.status === 'verworfen' && (
                  <div style={{ paddingLeft: 34 }}>
                    <button onClick={() => handleWiederherstellen(aktion)} disabled={aktionLaedt} data-tip="Wiederherstellen – Verworfene Empfehlung wieder anzeigen" style={knopf('leise')}>
                      Wiederherstellen
                    </button>
                  </div>
                )}
                {fehler[aktion.id] && <p role="alert" style={{ margin: 0, paddingLeft: 34, fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>{fehler[aktion.id]}</p>}
              </div>
            );
          })}
        </div>
      )}

      {zeigeWiedervorlageForm && (
        <div className="flex flex-col" style={{ ...kachel, gap: 8 }}>
          <div className="flex flex-wrap" style={{ gap: 6 }}>
            <input type="date" value={wvDatum} onChange={(e) => setWvDatum(e.target.value)} aria-label="Datum" data-tip="Datum – An diesem Tag erscheint das Ticket wieder" style={feld} />
            <input type="text" value={wvNotiz} onChange={(e) => setWvNotiz(e.target.value)} placeholder="Notiz (optional)" aria-label="Notiz"
              data-tip="Notiz – Wird bei der Wiedervorlage angezeigt" style={{ ...feld, flex: '1 1 160px', minWidth: 0 }} />
          </div>
          <div className="flex items-center" style={{ gap: 6 }}>
            <button onClick={handleWiedervorlageAnlegen} disabled={aktionLaedt || !wvDatum} data-tip="Anlegen – Wiedervorlage speichern"
              style={{ ...knopf('lila'), opacity: aktionLaedt || !wvDatum ? 0.5 : 1 }}>Anlegen</button>
            <button onClick={() => { setZeigeWiedervorlageForm(false); setWvDatum(''); setWvNotiz(''); setWvFehler(null); }} disabled={aktionLaedt} style={knopf('leise')}>
              <X size={12} weight="bold" /> Abbrechen
            </button>
          </div>
          {wvFehler && <p role="alert" style={{ margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>{wvFehler}</p>}
        </div>
      )}

      {/* Fußleiste: Vorschläge erzeugen · + Wiedervorlage · Antwortvorschlag erstellen */}
      <div className="flex flex-wrap items-center" style={{ gap: 6, paddingTop: 10, borderTop: '1px solid var(--line)' }}>
        {!hatOffenenEntwurf && (
          <button onClick={handleVorschlaegeErzeugen} disabled={aktionLaedt}
            data-tip="Vorschläge erzeugen – Die KI prüft das Ticket erneut und schlägt passende Aktionen vor" style={{ ...knopf('lila'), opacity: aktionLaedt ? 0.6 : 1 }}>
            {aktionLaedt ? <SpinnerGap size={13} className="animate-spin" /> : <Sparkle size={13} weight="fill" />} Vorschläge erzeugen
          </button>
        )}
        {!zeigeWiedervorlageForm && (
          <button onClick={() => setZeigeWiedervorlageForm(true)} data-tip="Wiedervorlage – Ticket an einem Tag wieder vorlegen" style={knopf('leise')}>
            <Plus size={12} weight="bold" /> Wiedervorlage
          </button>
        )}
        <button onClick={handleAntwortvorschlagErstellen} disabled={antwortDisabled}
          data-tip={ticket.ist_rechtlich_sensibel ? 'Rechtlich sensibel — bitte manuell antworten' : 'Antwortvorschlag erstellen – Entwurf einer Antwort an den Absender anlegen'}
          style={{ ...knopf('leise'), background: `color-mix(in srgb, ${LILA} 14%, transparent)`, color: LILA, opacity: antwortDisabled ? 0.45 : 1, cursor: antwortDisabled ? 'not-allowed' : 'pointer' }}>
          {antwortLaedt ? <SpinnerGap size={13} className="animate-spin" /> : <EnvelopeSimple size={13} weight="fill" />} Antwortvorschlag erstellen
        </button>
      </div>
      {erzeugenFehler && <p role="alert" style={{ margin: 0, fontSize: 12, fontWeight: 700, color: 'var(--bad)' }}>{erzeugenFehler}</p>}
      {erzeugenHinweis && <p style={{ margin: 0, fontSize: 12, color: 'var(--ink3)' }}>{erzeugenHinweis}</p>}
      {antwortHinweis && <p style={{ margin: 0, fontSize: 12, color: 'var(--ink3)' }}>{antwortHinweis}</p>}
    </div>
  );
};

export default TicketKiPanel;
